// Pure selectors mapping server-confirmed gameplay data → audio states. Shared, deterministic,
// side-effect free. Danger normalization is defined ONCE here and reused everywhere.
import { GameplayEvent } from "./gameplayAudioEvents";

export const EXPEDITION_NAME_TO_ID = {
  "the frozen pass": "frozen_pass",
  "temple of echoes": "temple_echoes",
  "the smuggler's coast": "smugglers_coast",
  "the smugglers coast": "smugglers_coast",
  "the gilded meridian": "gilded_meridian",
};

export function expeditionId(idOrName) {
  if (!idOrName) return null;
  const s = String(idOrName).toLowerCase();
  if (["frozen_pass", "temple_echoes", "smugglers_coast", "gilded_meridian"].includes(s)) return s;
  return EXPEDITION_NAME_TO_ID[s] || null;
}

// One canonical danger scale: calm | alert | danger | critical.
export function normalizeDanger(level) {
  if (typeof level === "number") {
    if (level >= 0.85) return "critical";
    if (level >= 0.6) return "danger";
    if (level >= 0.35) return "alert";
    return "calm";
  }
  const s = String(level || "").toLowerCase();
  if (["critical", "danger", "alert", "calm"].includes(s)) return s;
  return "calm";
}

export function dangerToState(danger) {
  return { calm: "expedition_exploration", alert: "strategic_decision", danger: "rising_danger", critical: "critical_danger" }[danger] || "expedition_exploration";
}

// Only ever called with the FINAL server-confirmed outcome label.
export function outcomeToState(label) {
  const s = String(label || "").toLowerCase();
  if (s.includes("critical")) return "critical_success";
  if (s.includes("narrow")) return "narrow_escape";
  if (s.includes("success")) return "success";
  return "failure"; // Failure / Major Loss
}

// Resolve a normalized gameplay event to an audio state (+ optional expedition + oneShot flag).
export function eventToAudioState(evt) {
  const t = evt.type;
  switch (t) {
    case GameplayEvent.ROUTE_ENTERED: return { state: evt.state || "dashboard", fallback: true };
    case GameplayEvent.EXPEDITION_SELECTED: return { state: "expedition_map", expedition: expeditionId(evt.expedition) };
    case GameplayEvent.CREW_CONFIRMED: return { state: "crew_selection", expedition: expeditionId(evt.expedition) };
    case GameplayEvent.STAKE_REVIEW_OPENED: return { state: "stake_review", expedition: expeditionId(evt.expedition) };
    case GameplayEvent.STAKE_CONFIRMED:
    case GameplayEvent.SESSION_STARTED: return { state: "expedition_exploration", expedition: expeditionId(evt.expedition) };
    case GameplayEvent.DECISION_PRESENTED:
    case GameplayEvent.DECISION_CONFIRMED: return { state: "strategic_decision", expedition: expeditionId(evt.expedition) };
    case GameplayEvent.DANGER_LEVEL_CHANGED: return { state: dangerToState(normalizeDanger(evt.level)), expedition: expeditionId(evt.expedition) };
    case GameplayEvent.SKILL_CHALLENGE_STARTED: return { state: "skill_challenge" };
    case GameplayEvent.SKILL_CHALLENGE_COMPLETED: return { state: "expedition_exploration", expedition: expeditionId(evt.expedition) };
    case GameplayEvent.OUTCOME_CONFIRMED: return { state: outcomeToState(evt.outcome), oneShot: true };
    case GameplayEvent.RECOVERY_STARTED:
    case GameplayEvent.RECOVERY_COMPLETED:
    case GameplayEvent.CREW_INJURED: return { state: "recovery_camp" };
    case GameplayEvent.RANK_PROMOTED: return { state: "rank_promotion", oneShot: true };
    case GameplayEvent.ACHIEVEMENT_UNLOCKED: return { state: "achievement_unlock", oneShot: true };
    case GameplayEvent.PRESTIGE_TIER_CHANGED: return { state: "prestige_ceremony", oneShot: true };
    case GameplayEvent.RIVAL_OVERTAKE_CONFIRMED: return { state: "rival_overtake", oneShot: true };
    case GameplayEvent.SEASON_FINALIZED: return { state: "season_finale", oneShot: true };
    case GameplayEvent.CONNECTION_LOST: return { state: "offline" };
    case GameplayEvent.CONNECTION_RESTORED: return { state: "reconnecting" };
    default: return { state: null };
  }
}
